import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { siteDefaults } from "@/lib/siteDefaults";
import { SingleImageUpload } from "@/components/ImageUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ArrowLeft, Save, RotateCcw, Plus, Trash2, Instagram, Rocket, Lock, Copy } from "lucide-react";

const cloneDeep = (o) => JSON.parse(JSON.stringify(o));

function Field({ label, value, onChange, testId, multiline, placeholder }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">{label}</Label>
      {multiline ? (
        <Textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          rows={4}
          placeholder={placeholder}
        />
      ) : (
        <Input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          placeholder={placeholder}
        />
      )}
    </div>
  );
}

export default function AdminSite() {
  const navigate = useNavigate();
  const { refresh } = useSite();
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem("jenova_admin_token")) {
      navigate("/admin/login");
      return;
    }
    api
      .get("/site")
      .then(({ data }) => {
        // merge into defaults so all keys exist for editing
        const merged = cloneDeep(siteDefaults);
        const applyOver = (dst, src) => {
          if (!src || typeof src !== "object") return;
          for (const k of Object.keys(src)) {
            if (typeof dst[k] === "object" && dst[k] !== null && !Array.isArray(dst[k])) {
              applyOver(dst[k], src[k] || {});
            } else if (src[k] !== undefined && src[k] !== null) {
              dst[k] = src[k];
            }
          }
        };
        applyOver(merged, data || {});
        setContent(merged);
      })
      .catch(() => setContent(cloneDeep(siteDefaults)));
    // eslint-disable-next-line
  }, []);

  const set = (path, val) => {
    setContent((prev) => {
      const next = cloneDeep(prev);
      const keys = path.split(".");
      let node = next;
      for (let i = 0; i < keys.length - 1; i++) {
        node[keys[i]] = node[keys[i]] || {};
        node = node[keys[i]];
      }
      node[keys[keys.length - 1]] = val;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/site", content);
      await refresh();
      toast.success("Site content saved. Reload the storefront to see it live.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const resetSection = (sectionKey) => {
    if (!window.confirm(`Reset "${sectionKey}" section to defaults?`)) return;
    setContent((prev) => {
      const next = cloneDeep(prev);
      next[sectionKey] = cloneDeep(siteDefaults[sectionKey]);
      return next;
    });
    toast.success(`${sectionKey} reset to defaults (save to persist)`);
  };

  if (!content) {
    return <div className="pt-32 text-center text-stone-500">Loading…</div>;
  }

  const g = (path) => path.split(".").reduce((o, k) => (o ? o[k] : undefined), content);

  return (
    <div className="pt-24 md:pt-28 pb-24 min-h-screen bg-white">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between border-b border-[#E5E2DC] pb-6">
          <div>
            <div className="flex items-center gap-3">
              <Link to="/admin" className="text-sm text-stone-500 hover:text-[#1A1C1A] inline-flex items-center gap-1">
                <ArrowLeft strokeWidth={1.5} className="w-4 h-4" /> Products
              </Link>
            </div>
            <p className="mt-2 text-xs uppercase tracking-[0.3em] text-stone-500 font-semibold">Studio</p>
            <h1 className="mt-2 font-serif text-4xl md:text-5xl font-light tracking-tight">
              Site <em className="italic text-stone-600">content</em>.
            </h1>
            <p className="mt-2 text-sm text-stone-500 max-w-lg">
              Edit every headline, image, and paragraph on the storefront. Changes go live instantly.
            </p>
          </div>
          <Button data-testid="admin-site-save" onClick={save} disabled={saving} className="bg-[#1A1C1A] hover:bg-[#3C3E3C] rounded-full">
            <Save className="w-4 h-4 mr-1" strokeWidth={1.5} /> {saving ? "Saving…" : "Save Changes"}
          </Button>
        </div>

        <Tabs defaultValue="hero" className="mt-8">
          <TabsList className="flex flex-wrap gap-2 h-auto bg-transparent p-0 mb-8 border-b border-[#E5E2DC] w-full justify-start rounded-none">
            <TabsTrigger value="launch" data-testid="tab-launch">Launch Mode</TabsTrigger>
            <TabsTrigger value="hero" data-testid="tab-hero">Hero</TabsTrigger>
            <TabsTrigger value="marquee" data-testid="tab-marquee">Marquee</TabsTrigger>
            <TabsTrigger value="categories" data-testid="tab-categories">Categories</TabsTrigger>
            <TabsTrigger value="featured" data-testid="tab-featured">Featured</TabsTrigger>
            <TabsTrigger value="brand_story" data-testid="tab-brand-story">Brand Story</TabsTrigger>
            <TabsTrigger value="about" data-testid="tab-about">About Page</TabsTrigger>
            <TabsTrigger value="footer" data-testid="tab-footer">Footer</TabsTrigger>
            <TabsTrigger value="instagram" data-testid="tab-instagram">Instagram</TabsTrigger>
          </TabsList>

          {/* LAUNCH MODE */}
          <TabsContent value="launch" className="space-y-6">
            <SectionHeader title="Launch Mode" onReset={() => resetSection("prelaunch")} />
            <div className="rounded-2xl border border-[#E5E2DC] p-6 md:p-8 bg-[#F9F9F6]">
              <div className="flex items-start gap-4">
                {g("launch_mode") === "prelaunch" ? (
                  <div className="w-12 h-12 rounded-full bg-[#1A1C1A] flex items-center justify-center flex-shrink-0"><Lock strokeWidth={1.5} className="w-5 h-5 text-[#F9F9F6]" /></div>
                ) : (
                  <div className="w-12 h-12 rounded-full bg-[#3F5F3F] flex items-center justify-center flex-shrink-0"><Rocket strokeWidth={1.5} className="w-5 h-5 text-white" /></div>
                )}
                <div className="flex-1">
                  <p className="text-xs uppercase tracking-[0.25em] text-stone-500 font-semibold mb-2">Current status</p>
                  <h3 className="font-serif text-2xl font-light">
                    {g("launch_mode") === "prelaunch" ? "Pre-launch (public shop hidden)" : "Live (shop open to everyone)"}
                  </h3>
                  <p className="mt-2 text-sm text-stone-600 leading-relaxed">
                    {g("launch_mode") === "prelaunch" ? (
                      <>Anyone visiting <strong>jenovacollective.co</strong> sees a "Coming Soon" splash. Only your founding-members signup link at <code className="bg-stone-200 px-1.5 py-0.5 rounded text-xs">/founding</code> is public.</>
                    ) : (
                      <>Your full shop is live — anyone can browse and buy. Founding members can still redeem their permanent 15% code at checkout.</>
                    )}
                  </p>
                </div>
              </div>

              <div className="mt-6 flex flex-col md:flex-row gap-3">
                <Button
                  data-testid="launch-mode-prelaunch"
                  onClick={() => set("launch_mode", "prelaunch")}
                  variant={g("launch_mode") === "prelaunch" ? "default" : "outline"}
                  className={`rounded-full ${g("launch_mode") === "prelaunch" ? "bg-[#1A1C1A] hover:bg-[#3C3E3C]" : ""}`}
                >
                  <Lock className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Pre-launch (hidden)
                </Button>
                <Button
                  data-testid="launch-mode-live"
                  onClick={() => set("launch_mode", "live")}
                  variant={g("launch_mode") === "live" ? "default" : "outline"}
                  className={`rounded-full ${g("launch_mode") === "live" ? "bg-[#3F5F3F] hover:bg-[#4A6A4A]" : ""}`}
                >
                  <Rocket className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Go live
                </Button>
                <p className="text-xs text-stone-500 self-center italic">Don't forget to Save Changes after switching.</p>
              </div>
            </div>

            <div className="rounded-2xl border border-[#E5E2DC] p-6">
              <p className="text-xs uppercase tracking-[0.25em] text-stone-500 font-semibold mb-4">Shareable founding link</p>
              <div className="flex items-center gap-2 border border-[#E5E2DC] rounded-full p-1.5 pl-5 bg-white">
                <code className="flex-1 font-mono text-sm select-all">https://jenovacollective.co/founding</code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText("https://jenovacollective.co/founding");
                    toast.success("Founding link copied");
                  }}
                  className="inline-flex items-center gap-1.5 bg-[#1A1C1A] text-[#F9F9F6] px-4 py-2 rounded-full text-xs uppercase tracking-wider font-medium hover:bg-[#3C3E3C]"
                >
                  <Copy className="w-3.5 h-3.5" strokeWidth={1.75} /> Copy
                </button>
              </div>
              <p className="mt-3 text-xs text-stone-500 leading-relaxed">
                Share this link with anyone — Instagram bio, texts, DMs, flyers, whatever. Works in both pre-launch and live modes.
              </p>
            </div>

            <div className="rounded-2xl border border-[#E5E2DC] p-6 md:p-8">
              <p className="text-xs uppercase tracking-[0.25em] text-stone-500 font-semibold mb-4">Coming-soon splash copy</p>
              <div className="grid md:grid-cols-2 gap-5 max-w-3xl">
                <Field label="Overline" value={g("prelaunch.overline")} onChange={(v) => set("prelaunch.overline", v)} testId="prelaunch-overline" />
                <div />
                <div className="grid grid-cols-3 gap-3 md:col-span-2">
                  <Field label="Title line 1" value={g("prelaunch.title_line_1")} onChange={(v) => set("prelaunch.title_line_1", v)} />
                  <Field label="Emphasis (italic)" value={g("prelaunch.title_em")} onChange={(v) => set("prelaunch.title_em", v)} />
                  <Field label="Title line 3" value={g("prelaunch.title_line_3")} onChange={(v) => set("prelaunch.title_line_3", v)} />
                </div>
                <Field label="Subtitle" value={g("prelaunch.subtitle")} onChange={(v) => set("prelaunch.subtitle", v)} multiline />
                <Field label="Button label" value={g("prelaunch.cta_label")} onChange={(v) => set("prelaunch.cta_label", v)} />
              </div>
            </div>
          </TabsContent>

          {/* HERO */}
          <TabsContent value="hero" className="space-y-6">
            <SectionHeader title="Homepage Hero" onReset={() => resetSection("hero")} />
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-5">
                <Field label="Badge text (small pill above the hero)" value={g("hero.badge_text")} onChange={(v) => set("hero.badge_text", v)} testId="hero-badge-text" />
                <Field label="Overline (small text above headline)" value={g("hero.overline")} onChange={(v) => set("hero.overline", v)} testId="hero-overline" />
                <div className="grid grid-cols-3 gap-3">
                  <Field label="Headline line 1" value={g("hero.title_line_1")} onChange={(v) => set("hero.title_line_1", v)} testId="hero-line1" />
                  <Field label="Emphasis (italic)" value={g("hero.title_em")} onChange={(v) => set("hero.title_em", v)} testId="hero-em" />
                  <Field label="Headline line 3" value={g("hero.title_line_3")} onChange={(v) => set("hero.title_line_3", v)} testId="hero-line3" />
                </div>
                <Field label="Subtitle paragraph" value={g("hero.subtitle")} onChange={(v) => set("hero.subtitle", v)} testId="hero-subtitle" multiline />
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Primary button label" value={g("hero.cta_primary")} onChange={(v) => set("hero.cta_primary", v)} testId="hero-cta1" />
                  <Field label="Primary button link" value={g("hero.cta_primary_href")} onChange={(v) => set("hero.cta_primary_href", v)} testId="hero-cta1-href" />
                  <Field label="Secondary button label" value={g("hero.cta_secondary")} onChange={(v) => set("hero.cta_secondary", v)} testId="hero-cta2" />
                  <Field label="Secondary button link" value={g("hero.cta_secondary_href")} onChange={(v) => set("hero.cta_secondary_href", v)} testId="hero-cta2-href" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">Background image</Label>
                <div className="mt-3">
                  <SingleImageUpload value={g("hero.image")} onChange={(v) => set("hero.image", v)} testId="hero-image" aspectRatio="aspect-[16/10]" />
                </div>
              </div>
            </div>
          </TabsContent>

          {/* MARQUEE */}
          <TabsContent value="marquee" className="space-y-6">
            <SectionHeader title="Marquee Ticker" onReset={() => resetSection("marquee")} />
            <div className="grid md:grid-cols-2 gap-6 max-w-2xl">
              <Field label="Primary phrase (large italic)" value={g("marquee.primary")} onChange={(v) => set("marquee.primary", v)} testId="marquee-primary" />
              <Field label="Secondary phrase (uppercase)" value={g("marquee.secondary")} onChange={(v) => set("marquee.secondary", v)} testId="marquee-secondary" />
            </div>
          </TabsContent>

          {/* CATEGORIES */}
          <TabsContent value="categories" className="space-y-6">
            <SectionHeader title="Homepage Category Tiles" onReset={() => resetSection("categories")} />
            <div className="grid md:grid-cols-2 gap-6 max-w-3xl">
              <Field label="Section overline" value={g("categories.overline")} onChange={(v) => set("categories.overline", v)} />
              <div />
              <Field label="Heading line 1" value={g("categories.heading_line_1")} onChange={(v) => set("categories.heading_line_1", v)} />
              <Field label="Heading emphasis" value={g("categories.heading_em")} onChange={(v) => set("categories.heading_em", v)} />
            </div>
            <div className="grid md:grid-cols-2 gap-10">
              <CategoryEditor label="Performance (Gym) tile" prefix="categories.gym" g={g} set={set} />
              <CategoryEditor label="Beauty tile" prefix="categories.beauty" g={g} set={set} />
            </div>
          </TabsContent>

          {/* FEATURED */}
          <TabsContent value="featured" className="space-y-6">
            <SectionHeader title="Featured Products Section" onReset={() => resetSection("featured")} />
            <div className="grid md:grid-cols-3 gap-4 max-w-3xl">
              <Field label="Overline" value={g("featured.overline")} onChange={(v) => set("featured.overline", v)} />
              <Field label="Heading line 1" value={g("featured.heading_line_1")} onChange={(v) => set("featured.heading_line_1", v)} />
              <Field label="Heading emphasis" value={g("featured.heading_em")} onChange={(v) => set("featured.heading_em", v)} />
            </div>
            <p className="text-xs text-stone-500 italic">Featured products themselves are managed on the Products page — toggle the "Featured on homepage" switch.</p>
          </TabsContent>

          {/* BRAND STORY */}
          <TabsContent value="brand_story" className="space-y-6">
            <SectionHeader title="Homepage Brand Story Block" onReset={() => resetSection("brand_story")} />
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <Field label="Overline" value={g("brand_story.overline")} onChange={(v) => set("brand_story.overline", v)} />
                <div className="grid grid-cols-3 gap-3">
                  <Field label="Heading line 1" value={g("brand_story.heading_line_1")} onChange={(v) => set("brand_story.heading_line_1", v)} />
                  <Field label="Emphasis" value={g("brand_story.heading_em")} onChange={(v) => set("brand_story.heading_em", v)} />
                  <Field label="Heading line 3" value={g("brand_story.heading_line_3")} onChange={(v) => set("brand_story.heading_line_3", v)} />
                </div>
                <Field label="Body paragraph" value={g("brand_story.body")} onChange={(v) => set("brand_story.body", v)} multiline />
                <Field label="CTA label" value={g("brand_story.cta_label")} onChange={(v) => set("brand_story.cta_label", v)} />
              </div>
              <div className="space-y-4">
                <Label className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">3 Images</Label>
                <SingleImageUpload value={g("brand_story.image_1")} onChange={(v) => set("brand_story.image_1", v)} testId="story-img-1" aspectRatio="aspect-[3/4]" />
                <SingleImageUpload value={g("brand_story.image_2")} onChange={(v) => set("brand_story.image_2", v)} testId="story-img-2" aspectRatio="aspect-[3/5]" />
                <SingleImageUpload value={g("brand_story.image_3")} onChange={(v) => set("brand_story.image_3", v)} testId="story-img-3" aspectRatio="aspect-[16/9]" />
              </div>
            </div>
          </TabsContent>

          {/* ABOUT */}
          <TabsContent value="about" className="space-y-8">
            <SectionHeader title="About Page" onReset={() => resetSection("about")} />
            <div className="space-y-4 max-w-3xl">
              <Field label="Overline" value={g("about.overline")} onChange={(v) => set("about.overline", v)} />
              <div className="grid grid-cols-3 gap-3">
                <Field label="Heading line 1" value={g("about.heading_line_1")} onChange={(v) => set("about.heading_line_1", v)} />
                <Field label="Emphasis" value={g("about.heading_em")} onChange={(v) => set("about.heading_em", v)} />
                <Field label="Heading line 3" value={g("about.heading_line_3")} onChange={(v) => set("about.heading_line_3", v)} />
              </div>
            </div>

            <SubSection title="Section 1 — The Name" prefix="about.section_1" g={g} set={set} />
            <SubSection title="Section 2 — The Center" prefix="about.section_2" g={g} set={set} withQuote />

            <div className="pt-6 border-t border-[#E5E2DC]">
              <p className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold mb-4">CTA Block (bottom of About page)</p>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Overline" value={g("about.cta.overline")} onChange={(v) => set("about.cta.overline", v)} />
                <div className="grid grid-cols-3 gap-3">
                  <Field label="Heading line 1" value={g("about.cta.title_line_1")} onChange={(v) => set("about.cta.title_line_1", v)} />
                  <Field label="Emphasis" value={g("about.cta.title_em")} onChange={(v) => set("about.cta.title_em", v)} />
                  <Field label="Heading line 3" value={g("about.cta.title_line_3")} onChange={(v) => set("about.cta.title_line_3", v)} />
                </div>
                <Field label="Body" value={g("about.cta.body")} onChange={(v) => set("about.cta.body", v)} multiline />
                <Field label="Button label" value={g("about.cta.cta_label")} onChange={(v) => set("about.cta.cta_label", v)} />
              </div>
            </div>
          </TabsContent>

          {/* FOOTER */}
          <TabsContent value="footer" className="space-y-6">
            <SectionHeader title="Footer" onReset={() => resetSection("footer")} />
            <div className="grid md:grid-cols-2 gap-6 max-w-3xl">
              <div className="grid grid-cols-3 gap-3 md:col-span-2">
                <Field label="Newsletter heading line 1" value={g("footer.newsletter_title_line_1")} onChange={(v) => set("footer.newsletter_title_line_1", v)} />
                <Field label="Emphasis" value={g("footer.newsletter_title_em")} onChange={(v) => set("footer.newsletter_title_em", v)} />
                <Field label="Heading line 3" value={g("footer.newsletter_title_line_3")} onChange={(v) => set("footer.newsletter_title_line_3", v)} />
              </div>
              <Field label="Newsletter body" value={g("footer.newsletter_body")} onChange={(v) => set("footer.newsletter_body", v)} multiline />
              <Field label="Tagline quote (bottom of footer)" value={g("footer.tagline_quote")} onChange={(v) => set("footer.tagline_quote", v)} multiline />
            </div>
          </TabsContent>

          {/* INSTAGRAM */}
          <TabsContent value="instagram" className="space-y-6">
            <SectionHeader title="Instagram Feed" onReset={() => resetSection("instagram")} />
            <div className="grid md:grid-cols-2 gap-6 max-w-3xl">
              <Field label="Instagram handle (no @)" value={g("instagram.handle")} onChange={(v) => set("instagram.handle", (v || "").replace(/^@/, ""))} testId="ig-handle" placeholder="jenovacollective" />
              <Field label="Follow-button label" value={g("instagram.cta_label")} onChange={(v) => set("instagram.cta_label", v)} testId="ig-cta" />
              <Field label="Overline (above heading)" value={g("instagram.overline")} onChange={(v) => set("instagram.overline", v)} />
              <div className="grid grid-cols-3 gap-3 md:col-span-2">
                <Field label="Heading line 1" value={g("instagram.heading_line_1")} onChange={(v) => set("instagram.heading_line_1", v)} />
                <Field label="Emphasis" value={g("instagram.heading_em")} onChange={(v) => set("instagram.heading_em", v)} />
                <Field label="Line 3" value={g("instagram.heading_line_3")} onChange={(v) => set("instagram.heading_line_3", v)} />
              </div>
              <Field label="Subtitle" value={g("instagram.subtitle")} onChange={(v) => set("instagram.subtitle", v)} multiline />
            </div>

            <InstagramPostsEditor
              posts={g("instagram.posts") || []}
              onChange={(next) => set("instagram.posts", next)}
            />

            <div className="rounded-xl border border-[#E5E2DC] bg-[#F9F9F6] p-5 mt-6 flex gap-4">
              <Instagram strokeWidth={1.5} className="w-5 h-5 text-stone-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-stone-600 font-light leading-relaxed">
                <strong className="text-[#1A1C1A] font-medium">How to get the post URL:</strong> open a post on Instagram → tap the "..." menu → "Copy link".
                It looks like <code className="font-mono text-xs bg-stone-200 px-1.5 py-0.5 rounded">https://instagram.com/p/AbC123/</code>. Paste it in the "Post link" field.
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <div className="mt-12 pt-8 border-t border-[#E5E2DC] flex justify-end">
          <Button onClick={save} disabled={saving} className="bg-[#1A1C1A] hover:bg-[#3C3E3C] rounded-full">
            <Save className="w-4 h-4 mr-1" strokeWidth={1.5} /> {saving ? "Saving…" : "Save Changes"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function SectionHeader({ title, onReset }) {
  return (
    <div className="flex items-center justify-between">
      <h2 className="font-serif text-2xl font-light">{title}</h2>
      <button
        onClick={onReset}
        className="text-xs uppercase tracking-wider text-stone-500 hover:text-[#1A1C1A] flex items-center gap-1.5"
      >
        <RotateCcw strokeWidth={1.5} className="w-3.5 h-3.5" /> Reset section
      </button>
    </div>
  );
}

function CategoryEditor({ label, prefix, g, set }) {
  return (
    <div className="space-y-4">
      <p className="text-sm font-semibold uppercase tracking-wide">{label}</p>
      <Field label="Overline" value={g(`${prefix}.overline`)} onChange={(v) => set(`${prefix}.overline`, v)} />
      <Field label="Title" value={g(`${prefix}.title`)} onChange={(v) => set(`${prefix}.title`, v)} />
      <Field label="Subtitle" value={g(`${prefix}.subtitle`)} onChange={(v) => set(`${prefix}.subtitle`, v)} multiline />
      <Field label="CTA label" value={g(`${prefix}.cta_label`)} onChange={(v) => set(`${prefix}.cta_label`, v)} />
      <Label className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">Image</Label>
      <SingleImageUpload value={g(`${prefix}.image`)} onChange={(v) => set(`${prefix}.image`, v)} testId={`${prefix}-image`} aspectRatio="aspect-[4/5]" />
    </div>
  );
}

function SubSection({ title, prefix, g, set, withQuote }) {
  return (
    <div className="pt-6 border-t border-[#E5E2DC]">
      <p className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold mb-4">{title}</p>
      <div className="grid md:grid-cols-2 gap-8">
        <div className="space-y-4">
          <Field label="Overline" value={g(`${prefix}.overline`)} onChange={(v) => set(`${prefix}.overline`, v)} />
          <div className="grid grid-cols-3 gap-3">
            <Field label="Title line 1" value={g(`${prefix}.title_line_1`)} onChange={(v) => set(`${prefix}.title_line_1`, v)} />
            <Field label="Emphasis" value={g(`${prefix}.title_em`)} onChange={(v) => set(`${prefix}.title_em`, v)} />
            <Field label="Line 3" value={g(`${prefix}.title_line_3`)} onChange={(v) => set(`${prefix}.title_line_3`, v)} />
          </div>
          <Field label="Body paragraph" value={g(`${prefix}.body`)} onChange={(v) => set(`${prefix}.body`, v)} multiline />
          {withQuote && (
            <>
              <Field label="Quote text" value={g(`${prefix}.quote_text`)} onChange={(v) => set(`${prefix}.quote_text`, v)} multiline />
              <Field label="Quote source" value={g(`${prefix}.quote_source`)} onChange={(v) => set(`${prefix}.quote_source`, v)} />
            </>
          )}
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">Image</Label>
          <div className="mt-3">
            <SingleImageUpload value={g(`${prefix}.image`)} onChange={(v) => set(`${prefix}.image`, v)} testId={`${prefix}-image`} aspectRatio="aspect-[4/5]" />
          </div>
        </div>
      </div>
    </div>
  );
}

function InstagramPostsEditor({ posts, onChange }) {
  const MAX = 9;
  const update = (idx, key, val) => {
    const next = [...posts];
    next[idx] = { ...next[idx], [key]: val };
    onChange(next);
  };
  const remove = (idx) => onChange(posts.filter((_, i) => i !== idx));
  const add = () => {
    if (posts.length >= MAX) return;
    onChange([...posts, { image: "", url: "", caption: "" }]);
  };

  return (
    <div className="pt-6 border-t border-[#E5E2DC]">
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">Posts ({posts.length}/{MAX})</p>
        <button
          type="button"
          onClick={add}
          disabled={posts.length >= MAX}
          data-testid="ig-add-post"
          className="inline-flex items-center gap-1.5 border border-[#1A1C1A] hover:bg-[#1A1C1A] hover:text-[#F9F9F6] px-4 py-2 rounded-full text-xs uppercase tracking-wider transition-colors disabled:opacity-50"
        >
          <Plus strokeWidth={1.75} className="w-3.5 h-3.5" /> Add post
        </button>
      </div>

      {posts.length === 0 && (
        <p className="text-sm text-stone-500 italic font-serif py-8 text-center border border-dashed border-stone-300 rounded-xl">
          No posts yet. Click "Add post" to feature your Instagram photos on the homepage.
        </p>
      )}

      <div className="grid md:grid-cols-2 gap-5">
        {posts.map((p, i) => (
          <div key={i} data-testid={`ig-post-${i}`} className="border border-[#E5E2DC] rounded-xl p-4 md:p-5 bg-white space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Post {i + 1}</p>
              <button
                type="button"
                onClick={() => remove(i)}
                data-testid={`ig-remove-post-${i}`}
                className="inline-flex items-center gap-1 text-xs text-stone-500 hover:text-[#8B3A3A]"
              >
                <Trash2 strokeWidth={1.5} className="w-3.5 h-3.5" /> Remove
              </button>
            </div>
            <SingleImageUpload
              value={p.image}
              onChange={(v) => update(i, "image", v)}
              testId={`ig-post-image-${i}`}
              aspectRatio="aspect-square"
            />
            <div className="space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">Post link (Instagram URL)</Label>
                <Input
                  value={p.url || ""}
                  onChange={(e) => update(i, "url", e.target.value)}
                  data-testid={`ig-post-url-${i}`}
                  placeholder="https://instagram.com/p/AbC123/"
                  className="mt-2"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">Caption (optional — shown on hover)</Label>
                <Textarea
                  rows={2}
                  value={p.caption || ""}
                  onChange={(e) => update(i, "caption", e.target.value)}
                  data-testid={`ig-post-caption-${i}`}
                  className="mt-2"
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
